import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { tap } from 'rxjs';
import { FileHelper } from '../../../../../../core/helpers/file/file-helper';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { AccountingReviewReportRequest } from '../../../../shared/models/accounting-review-report-request';
import { ReportService } from '../../../../shared/services/report.service';
import { AccountingReviewReportConstants } from './constants/accounting-review-report-constants';
import { AccountingReviewReportFormModel } from './models/accounting-review-report-form-model';
import { AccountingReviewReportFormService } from './services/accounting-review-report-form.service';

@Component({
    selector: 'app-accounting-review-report',
    templateUrl: './accounting-review-report.component.html',
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatSelectModule,
        MatButtonModule,
    ],
    providers: [provideNativeDateAdapter()],
})
export class AccountingReviewReportComponent {
    private readonly reportService = inject(ReportService);
    private readonly dropdownService = inject(DropdownService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly accountingReviewReportFormService = inject(
        AccountingReviewReportFormService,
    );

    form: FormGroup<FormGroupType<AccountingReviewReportFormModel>> =
        this.accountingReviewReportFormService.getFormDefinition();

    accountingReviewReportExportTypeDropdownOptions =
        this.dropdownService.getAccountingReviewReportExportTypeOptions();

    /**
     * The max date that can be selected in the date range picker.
     * This is set to the current date to prevent users from selecting future dates, which may not have relevant data for the report.
     */
    readonly maxDate = new Date();

    onExport(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.reportService
            .exportAccountingReviewReport(
                this.generateAccountingReviewReportRequestObject(),
            )
            .pipe(
                tap((response) => {
                    const blob = response.body;
                    if (!blob) {
                        return;
                    }

                    const filename =
                        FileHelper.getFilenameFromContentDisposition(
                            response,
                        ) ?? AccountingReviewReportConstants.DEFAULT_FILENAME;
                    FileHelper.downloadBlobFile(blob, filename);
                }),
                tap(() => {
                    this.resetForm();

                    // Trigger change detection to update the form in the UI after resetting it
                    this.cdr.detectChanges();
                }),
            )
            .subscribe();
    }

    /**
     * Clears the start and end date fields in the form. If either of the date fields has a value, it sets both fields to null without emitting any events.
     */
    clearDates(): void {
        if (
            this.form.controls.startDate.value ||
            this.form.controls.endDate.value
        ) {
            this.form.patchValue(
                { startDate: null, endDate: null },
                { emitEvent: false },
            );
        }
    }

    private generateAccountingReviewReportRequestObject(): AccountingReviewReportRequest {
        const formValue = this.form
            .value as Required<AccountingReviewReportRequest>;

        return { ...formValue };
    }

    /**
     * Resets the form to its initial state by reinitializing it with the default form definition.
     * This is called after a successful export to ensure that the form is cleared and ready for new input.
     */
    private resetForm(): void {
        this.form = this.accountingReviewReportFormService.getFormDefinition();
    }
}
